import React from 'react';
import './rush.css';
import './features.css';
import './feature-detail.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { FI, FeatHero, FeatCapabilities, FeatDetailRow, FeatSteps, FeatQuote, FeatCrossLinks, FeatFinalCTA } from './RushFeatureLayout';

const RISK = '#d2604f';
const TIGHT = '#e0a92a';
const OK = 'var(--green)';

// A schematic page echoing the in-app layout map: frames at their real position,
// coloured by predicted fit, the CTA overflowing in German.
const LRMapVisual = () => {
  // [left%, top%, w%, h%, status, label, fill%]
  const frames = [
    [8, 7, 84, 13, 'ok', 'Kicker', 40],
    [8, 24, 84, 16, 'ok', 'Headline', 62],
    [8, 44, 55, 30, 'ok', 'Intro deck', 70],
    [66, 44, 26, 30, 'ok', 'Image caption', 30],
    [8, 78, 50, 8, 'tight', 'Offer banner', 92],
    [62, 78, 30, 8, 'over', 'CTA button', 100],
  ];
  const color = (s) => (s === 'over' ? RISK : s === 'tight' ? TIGHT : OK);
  return (
    <div className="mock-frame">
      <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Layout risk · DE · pre-flight</span></div>
      <div style={{ padding: 16, display: 'flex', gap: 14 }}>
        {/* page canvas */}
        <div style={{ position: 'relative', width: 240, height: 300, background: '#fffdf8', border: '1px solid rgba(0,0,0,0.12)', borderRadius: 3, boxShadow: '0 10px 30px rgba(20,17,12,0.12)', flex: '0 0 auto' }}>
          {frames.map(([l, t, w, h, s, label, fill]) => (
            <div key={label} title={label} style={{
              position: 'absolute', left: `${l}%`, top: `${t}%`, width: `${w}%`, height: `${h}%`,
              border: `1.5px solid ${color(s)}`, borderRadius: 3, overflow: 'hidden', background: 'var(--bg-card)',
            }}>
              <span style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: `${Math.min(fill, 100)}%`, background: `color-mix(in oklab, ${color(s)} ${s === 'ok' ? 22 : 42}%, transparent)` }} />
              {fill >= 100 && s === 'over' && (
                <span style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '34%', background: 'repeating-linear-gradient(45deg, rgba(210,96,79,.55) 0 3px, rgba(210,96,79,.18) 3px 6px)' }} />
              )}
              {(s === 'over' || s === 'tight') && (
                <span style={{ position: 'absolute', top: 2, right: 2, width: 6, height: 6, borderRadius: '50%', background: color(s), boxShadow: '0 0 0 1.5px #fffdf8' }} />
              )}
              <span style={{ position: 'absolute', left: 4, top: 2, fontSize: 7.5, fontWeight: 700, color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>{label}</span>
            </div>
          ))}
        </div>
        {/* mini panel */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--ink)' }}>German runs ≈ +35%</div>
          {[['CTA button', "won't fit", RISK, '#fbe1e1'], ['Offer banner', 'tight fit', TIGHT, 'var(--yellow-soft)']].map(([nm, st, c, bg]) => (
            <div key={nm} style={{ border: '1px solid var(--line)', borderLeft: `3px solid ${c}`, borderRadius: 8, padding: '7px 9px', background: 'var(--bg-soft)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 650, color: 'var(--ink)' }}>{nm}</span>
                <span style={{ marginLeft: 'auto', fontSize: 9, fontWeight: 700, color: c, background: bg, padding: '1px 7px', borderRadius: 999 }}>{st}</span>
              </div>
            </div>
          ))}
          <div style={{ marginTop: 'auto', fontSize: 10.5, color: 'var(--ink-4)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>{FI.alert} 2 frames at risk · before translation</div>
        </div>
      </div>
    </div>
  );
};

// The Auto-fit card: a fit-constrained AI draft sized to the frame.
const LRAutofitVisual = () => (
  <div className="mock-frame">
    <div className="mock-chrome"><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-dot"></span><span className="mc-title">Auto-fit · CTA button · DE</span></div>
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ border: '1px solid var(--line)', borderLeft: `3px solid ${RISK}`, borderRadius: 9, padding: 11, background: 'var(--bg-soft)' }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--ink-4)', textTransform: 'uppercase' }}>Direct translation</div>
        <div style={{ fontSize: 12.5, color: 'var(--ink)', marginTop: 3 }}>Entdecken Sie die Kollektion</div>
        <div style={{ fontSize: 10.5, color: RISK, fontWeight: 600, marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5 }}>{FI.alert} 28 / 24 chars — overflows</div>
      </div>
      <div style={{ border: `1px solid color-mix(in oklab, var(--accent) 35%, var(--line))`, borderRadius: 9, padding: 11, background: 'color-mix(in oklab, var(--accent) 5%, var(--bg-card))' }}>
        <div style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--accent)', textTransform: 'uppercase' }}>Auto-fit · Rush AI</div>
        <div style={{ fontSize: 13, color: 'var(--ink)', fontWeight: 600, marginTop: 3 }}>Zur Kollektion</div>
        <div style={{ fontSize: 10.5, color: 'var(--green)', fontWeight: 600, marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 5 }}>{FI.check} Fits — 14 / 24 chars · glossary kept</div>
        <div style={{ display: 'flex', gap: 6, marginTop: 9 }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: 'var(--ink)', padding: '5px 12px', borderRadius: 8 }}>Apply</span>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-3)', border: '1px solid var(--line-2)', padding: '5px 12px', borderRadius: 8 }}>Discard</span>
        </div>
      </div>
    </div>
  </div>
);

const RushFeatureLayoutRisk = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav active="features" />
      <FeatHero
        kicker="Pre-flight layout risk"
        kickerIcon={FI.alert}
        title="Know which layouts break — before you translate a word."
        lead="Rush pseudo-translates every text frame at each language's real expansion rate and predicts, up front, which ones will overflow. It reads the actual geometry from your IDML, DOCX, PPTX, and XLSX — frame bounds, font size, leading — so the warning lands before the work starts, not after the file is back from the printer."
        primary={{ label: 'Get early access', to: '/register' }}
        secondary={{ label: 'See all features', to: '/features' }}
        meta={['IDML · DOCX · PPTX · XLSX', 'Predicted before translation', 'AI Auto-fit to the frame']}
        visual={<LRMapVisual />}
      />

      <FeatCapabilities
        eyebrow="What it does"
        title="The overflow you'd find at the printer — found at kickoff."
        lead="German runs ~35% longer than English. Finnish, 22%. Rush turns those rules of thumb into a per-frame verdict against your real document, then drafts translations that actually fit."
        caps={[
          { icon: FI.alert, title: 'Pre-flight prediction', desc: 'Every frame gets a fits / tight / won’t-fit verdict at each target language — before a single word is translated.' },
          { icon: FI.layers, title: 'Reads real geometry', desc: 'Frame bounds, point size, and leading parsed straight from the source file — not a guess from an abstract string list.' },
          { icon: FI.gauge, title: 'Learned expansion', desc: 'Starts from industry expansion factors, then sharpens to your team’s own measured ratios as projects complete.' },
          { icon: FI.bolt, title: 'AI Auto-fit', desc: 'One click drafts a translation constrained to the space — glossary terms kept verbatim, brand voice intact.' },
        ]}
      />

      <FeatDetailRow
        reverse
        eyebrow="The layout map"
        title="See exactly where it breaks — on the page."
        intro="Every frame is drawn at its true position and size, filled by how much translated text it will hold. The German CTA spilling out of its button is red on the map and red in the panel; click either and you jump straight to it. No more discovering the break in a PDF proof."
        points={[
          { icon: FI.eye, title: 'True-to-layout map', desc: 'Frames placed where they actually sit, coloured by predicted fit — a glance tells you the whole spread.' },
          { icon: FI.alert, title: 'Per-frame status', desc: 'Won’t fit / tight / fits, with predicted vs. available characters on every frame.' },
          { icon: FI.history, title: 'Re-run any time', desc: 'Switch language, re-run pre-flight, or check it live against the real translation as it lands.' },
        ]}
        replaces={['Printer proofs', 'Post-translation layout bug reports']}
        visual={<LRMapVisual />}
      />

      <FeatDetailRow
        eyebrow="Auto-fit"
        title="Don’t just flag it — fix it to the frame."
        intro="When a frame won’t hold the translation, Auto-fit drafts one that does. It translates against a character budget, measures the result, and retries tighter until it fits — keeping locked glossary terms verbatim and honouring your brand voice. Apply it in place, or discard and write your own."
        points={[
          { icon: FI.bolt, title: 'Fit-constrained drafting', desc: 'A measure-and-retry loop targets the exact space the frame allows.' },
          { icon: FI.lock, title: 'Glossary-safe', desc: 'Locked terms survive verbatim; brand voice rules still apply to the rest.' },
          { icon: FI.check, title: 'Apply or discard', desc: 'Accept the fitted draft into the project, or keep it as a starting point for a human.' },
        ]}
        replaces={['Manual re-writing to fit', '“Can you make this shorter?” threads']}
        visual={<LRAutofitVisual />}
        soft
      />

      <FeatSteps
        eyebrow="How it works"
        title="From source file to safe layout."
        steps={[
          { h: 'Read the real frames', p: 'On upload, Rush parses the actual frame geometry and type metrics from your IDML/DOCX/PPTX/XLSX.' },
          { h: 'Pseudo-translate & predict', p: 'Each frame is expanded at the target language’s rate and compared to the space it has — fits, tight, or over.' },
          { h: 'Auto-fit what breaks', p: 'Draft a translation sized to the frame, keep your terms, and apply it — long before anything goes to print.' },
        ]}
      />

      <FeatQuote quote="We used to catch German overflow at the proof stage, days late. Now the broken frames are flagged the moment the file lands — and Auto-fit usually has a version that fits before I even ask." initials="MR" name="Maren Roth" role="Localization lead, Atlas Print" />

      <FeatCrossLinks current="layout-risk" />

      <FeatFinalCTA source="feature-layout-risk" title="Catch the overflow at kickoff, not at the printer." sub="Join the waitlist for early-access pricing and a 1:1 onboarding call with our team." />

      <RushFooter />
    </div>
  );
};

export default RushFeatureLayoutRisk;
